// ============================================================
// MusicPicker.jsx — royalty-free music library + upload (Option C)
// ------------------------------------------------------------
// Two sources, one selection model (both store a direct audio URL on the
// project's music state, which the render payload forwards to FFmpeg):
//   1. Pre-seeded library (musicLibrary.js — Pixabay Audio). Each track has an
//      inline play/pause preview.
//   2. "Upload your own" — posts to the backend, which stores the file in the
//      'music-uploads' bucket and returns a public URL.
//
// ============================================================

import React, { useEffect, useRef, useState } from 'react';
import { clientScopedFetch } from '../../lib/authedFetch';
import { musicLibrary } from '../../utils/musicLibrary';
import { fmtTime } from '../../utils/mockVideoAssets';

const BACKEND =
  process.env.REACT_APP_BACKEND_URL || 'https://autopilot-backend-pn9r.onrender.com';

const MAX_AUDIO_BYTES = 20 * 1024 * 1024; // 20MB
const AUDIO_ACCEPT = '.mp3,.m4a,.aac,.wav,.ogg,audio/*';

export default function MusicPicker({ music, onSelect, onUpdate, clientId, showToast }) {
  const [mood, setMood]         = useState('All');
  const [playingId, setPlayingId] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError]       = useState(null);
  const audioRef = useRef(null);
  const fileInputRef = useRef(null);

  const genres = ['All', ...new Set(musicLibrary.map((t) => t.genre))];
  const tracks = mood === 'All' ? musicLibrary : musicLibrary.filter((t) => t.genre === mood);

  // Lazily create one shared <audio> for previews; clear playing state on end.
  const getAudio = () => {
    if (!audioRef.current) {
      const a = new Audio();
      a.addEventListener('ended', () => setPlayingId(null));
      a.addEventListener('error', () => setPlayingId(null));
      audioRef.current = a;
    }
    return audioRef.current;
  };

  // Stop preview playback when the picker unmounts (e.g. switching tabs).
  useEffect(() => () => {
    if (audioRef.current) { audioRef.current.pause(); audioRef.current.src = ''; }
  }, []);

  const togglePreview = (track, e) => {
    e.stopPropagation(); // don't also (de)select the track
    const a = getAudio();
    if (playingId === track.id) {
      a.pause();
      setPlayingId(null);
      return;
    }
    a.src = track.url;
    a.play().then(() => setPlayingId(track.id)).catch(() => {
      setError('Could not play this preview.');
      setPlayingId(null);
    });
  };

  // ── Upload your own ──────────────────────────────────────────────────────────
  const onFileChange = (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setError(null);

    if (file.size > MAX_AUDIO_BYTES) { setError('That audio file is over the 20MB limit.'); return; }
    if (!clientId) { setError('Still loading your workspace — try again in a moment.'); return; }

    const form = new FormData();
    form.append('file', file);
    // client_id REMOVED (Phase 7). This file has no direct-Supabase call sites
    // so it never appeared in the migration's count, but it sat in the same
    // panel and sent the same field — and the backend let a multipart
    // client_id beat req.clientId for admin callers. Adding ?client= without
    // removing this would have kept uploads landing under the admin's prefix.
    // The route stopped reading the form body in the same change.

    setUploading(true);
    clientScopedFetch(`${BACKEND}/api/video/upload-music`, { method: 'POST', body: form })
      .then(async (res) => {
        const json = await res.json().catch(() => ({}));
        if (!res.ok || !json.url) throw new Error(json.error || `Upload failed (${res.status})`);
        const title = (file.name || 'My upload').replace(/\.[^.]+$/, '');
        onSelect?.({ id: `upload-${Date.now()}`, title, artist: 'Your upload', duration: 0, url: json.url });
        if (showToast) showToast('Music uploaded ✓');
      })
      .catch((err) => setError(err.message))
      .finally(() => setUploading(false));
  };

  return (
    <div className="ve-music">
      <div className="ve-field-label">Mood</div>
      <div className="ve-chip-row">
        {genres.map((m) => (
          <button
            key={m}
            className={`ve-chip${mood === m ? ' active' : ''}`}
            onClick={() => setMood(m)}
          >
            {m}
          </button>
        ))}
      </div>

      <div className="ve-field-label">Library <span className="ve-mock-tag">Pixabay</span></div>
      {error && <div className="ve-browser-error" style={{ marginBottom: 10 }}>{error}</div>}
      <div className="ve-track-list">
        {tracks.map((t) => {
          const selected = music?.trackId === t.id;
          const isPlaying = playingId === t.id;
          return (
            <button
              key={t.id}
              className={`ve-track${selected ? ' selected' : ''}`}
              onClick={() => onSelect?.(selected ? null : t)}
            >
              <div className="ve-track-head">
                <span
                  className="ve-track-play"
                  role="button"
                  aria-label={isPlaying ? 'Pause preview' : 'Play preview'}
                  title={isPlaying ? 'Pause' : 'Play'}
                  onClick={(e) => togglePreview(t, e)}
                >
                  {isPlaying ? '❚❚' : '▶'}
                </span>
                <div className="ve-track-meta">
                  <span className="ve-track-title">{t.title}</span>
                  <span className="ve-track-sub">{t.artist} · {t.genre} · {fmtTime(t.duration)}</span>
                </div>
                {selected && <span className="ve-track-check" aria-hidden="true">✓</span>}
              </div>
            </button>
          );
        })}
      </div>

      <div className="ve-field-label">Upload your own</div>
      <input
        ref={fileInputRef}
        type="file"
        accept={AUDIO_ACCEPT}
        onChange={onFileChange}
        style={{ display: 'none' }}
      />
      <button
        className="ve-btn ve-btn-ghost ve-btn-block"
        onClick={() => fileInputRef.current?.click()}
        disabled={uploading || !clientId}
      >
        {uploading ? 'Uploading…' : '＋ Upload audio (MP3, max 20MB)'}
      </button>

      {music && (
        <div className="ve-music-controls">
          <div className="ve-field-label">Selected</div>
          <div className="ve-track-sub" style={{ marginBottom: 8 }}>
            {music.title}{music.artist ? ` · ${music.artist}` : ''}
          </div>
          <div className="ve-field-label">Volume</div>
          <div className="ve-slider-row">
            <input
              className="ve-range"
              type="range"
              min={0}
              max={100}
              value={music.volume}
              onChange={(e) => onUpdate({ volume: Number(e.target.value) })}
            />
            <span className="ve-slider-val">{music.volume}%</span>
          </div>
          <button className="ve-link-btn" onClick={() => onSelect(null)}>Remove track</button>
        </div>
      )}
    </div>
  );
}
